import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StepProps } from '../MultiStepModal';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const inferGroupCandidate = (label: string) => {
  const s = String(label || '').trim();
  if (!s) return null;
  const m = s.match(/^([A-Za-z][A-Za-z ]{1,30})\s*(\d{1,2})\b/);
  if (!m) return null;
  const base = String(m[1] || '').replace(/\s+/g, ' ').trim();
  const num = String(m[2] || '').trim();
  if (!base || !num) return null;
  return `${base} ${num}`;
};

const BUILD_TAG = 'build-2026-02-04-5';

export default function CertificateTemplatePreviewGroupStep({ data, onDataChange }: StepProps) {
  const fieldsRaw = (data as any)?.fields;
  const templatePagesRaw = (data as any)?.templatePages;
  const fields: any[] = useMemo(() => {
    return Array.isArray(fieldsRaw) ? fieldsRaw : [];
  }, [fieldsRaw]);
  const templatePages: any[] = useMemo(() => {
    return Array.isArray(templatePagesRaw) ? templatePagesRaw : [];
  }, [templatePagesRaw]);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [groupNameInput, setGroupNameInput] = useState('');
  const [existingGroupSelected, setExistingGroupSelected] = useState<string>('');
  const [renameFrom, setRenameFrom] = useState<string>('');
  const [renameTo, setRenameTo] = useState<string>('');

  const previewUrl = String(data.templatePreviewUrl || '').trim();

  const [pdfError, setPdfError] = useState<string>('');

  const [numPages, setNumPages] = useState<number>(0);
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [pageDims, setPageDims] = useState<{ xMin: number; yMin: number; width: number; height: number } | null>(null);
  const [overlaySize, setOverlaySize] = useState<{ width: number; height: number } | null>(null);
  const pageContainerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const effectivePageWidth = pageWidth || 720;

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
  const dragMovedRef = useRef(false);

  const selectionCount = Object.values(selected).filter(Boolean).length;

  const groupSummary = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of fields) {
      const g = String(f?.groupName || '').trim();
      if (!g) continue;
      m.set(g, (m.get(g) || 0) + 1);
    }
    return Array.from(m.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [fields]);

  const existingGroups = useMemo(() => groupSummary.map((g) => g.name), [groupSummary]);

  const updateFields = (next: any[]) => {
    onDataChange({ fields: next });
  };

  useEffect(() => {
    if (!existingGroups.length) return;
    if (!existingGroupSelected) {
      setExistingGroupSelected(existingGroups[0]);
    }
    if (!renameFrom) {
      setRenameFrom(existingGroups[0]);
    }
  }, [existingGroups, existingGroupSelected, renameFrom]);

  useEffect(() => {
    const el = pageContainerRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setPageWidth(w);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    const update = () => {
      const r = el.getBoundingClientRect();
      const w = Number(r.width);
      const h = Number(r.height);
      if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
        setOverlaySize({ width: w, height: h });
      }
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentPageIndex, effectivePageWidth]);

  useEffect(() => {
    const fromMeta = templatePages?.[currentPageIndex];
    const w = Number(fromMeta?.width);
    const h = Number(fromMeta?.height);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      const xMin = Number(fromMeta?.xMin);
      const yMin = Number(fromMeta?.yMin);
      setPageDims({
        xMin: Number.isFinite(xMin) ? xMin : 0,
        yMin: Number.isFinite(yMin) ? yMin : 0,
        width: w,
        height: h,
      });
      return;
    }
  }, [templatePages, currentPageIndex]);

  const toggleAll = (checked: boolean) => {
    const next: Record<number, boolean> = {};
    if (checked) {
      fields.forEach((_, idx) => {
        next[idx] = true;
      });
    }
    setSelected(next);
  };

  const applyGroupToSelection = (raw: string) => {
    const name = String(raw || '').replace(/\s+/g, ' ').trim().slice(0, 120);
    if (!name) return;

    const next = [...fields];
    next.forEach((f, idx) => {
      if (!selected[idx]) return;
      next[idx] = { ...f, groupName: name };
    });

    updateFields(next);
  };

  const currentPageFields = useMemo(() => {
    return fields
      .map((f, idx) => ({ f, idx }))
      .filter(({ f }) => {
        const p = typeof f?.pageIndex === 'number' && Number.isFinite(f.pageIndex) ? f.pageIndex : 0;
        return p === currentPageIndex;
      });
  }, [fields, currentPageIndex]);

  const renderHeight = useMemo(() => {
    if (!pageDims?.width || !pageDims?.height || !effectivePageWidth) return 0;
    return (pageDims.height / pageDims.width) * effectivePageWidth;
  }, [pageDims, effectivePageWidth]);

  const screenBox = useMemo(() => {
    const w = Number(overlaySize?.width) || 0;
    const h = Number(overlaySize?.height) || 0;
    return {
      width: w > 0 ? w : effectivePageWidth,
      height: h > 0 ? h : renderHeight,
    };
  }, [overlaySize, effectivePageWidth, renderHeight]);

  const toScreenRect = (f: any) => {
    const pageW = pageDims?.width;
    const pageH = pageDims?.height;
    const xMin = pageDims?.xMin ?? 0;
    const yMin = pageDims?.yMin ?? 0;
    const x = Number(f?.x);
    const y = Number(f?.y);
    const w = Number(f?.width);
    const h = Number(f?.height);
    const screenW = Number(screenBox.width);
    const screenH = Number(screenBox.height);
    if (!pageW || !pageH || !screenW || !screenH) return null;
    if (![x, y, w, h].every((n) => typeof n === 'number' && Number.isFinite(n))) return null;

    const left = ((x - xMin) / pageW) * screenW;
    const top = screenH - (((y - yMin) + h) / pageH) * screenH;
    const width = (w / pageW) * screenW;
    const height = (h / pageH) * screenH;
    return { left, top, width, height };
  };

  const rectIntersects = (
    a: { left: number; top: number; width: number; height: number },
    b: { left: number; top: number; width: number; height: number },
  ) => {
    const aR = a.left + a.width;
    const aB = a.top + a.height;
    const bR = b.left + b.width;
    const bB = b.top + b.height;
    return !(aR < b.left || bR < a.left || aB < b.top || bB < a.top);
  };

  const getOverlayPoint = (e: any) => {
    const el = overlayRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const src = e?.nativeEvent ?? e;
    const clientX =
      src?.clientX ??
      src?.touches?.[0]?.clientX ??
      src?.changedTouches?.[0]?.clientX ??
      src?.targetTouches?.[0]?.clientX;
    const clientY =
      src?.clientY ??
      src?.touches?.[0]?.clientY ??
      src?.changedTouches?.[0]?.clientY ??
      src?.targetTouches?.[0]?.clientY;

    const x = Number(clientX) - r.left;
    const y = Number(clientY) - r.top;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  };

  const handlePointerDown = (e: any) => {
    const btn = typeof e?.button === 'number' ? e.button : 0;
    if (btn !== 0) return;
    e.preventDefault();
    dragMovedRef.current = false;
    try {
      (e.currentTarget as any).setPointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }
    const pt = getOverlayPoint(e);
    if (!pt) return;
    setIsDragging(true);
    setDragStart(pt);
    setDragCurrent(pt);
  };

  const handlePointerMove = (e: any) => {
    if (!isDragging) return;
    e.preventDefault();
    const pt = getOverlayPoint(e);
    if (!pt) return;
    if (dragStart) {
      const dx = Math.abs(pt.x - dragStart.x);
      const dy = Math.abs(pt.y - dragStart.y);
      if (dx > 3 || dy > 3) dragMovedRef.current = true;
    }
    setDragCurrent(pt);
  };

  const handlePointerUp = (e?: any) => {
    if (e) {
      e.preventDefault();
      try {
        (e.currentTarget as any).releasePointerCapture?.(e.pointerId);
      } catch {
        // ignore
      }
    }
    if (!isDragging || !dragStart || !dragCurrent) {
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
      return;
    }

    const left = Math.min(dragStart.x, dragCurrent.x);
    const top = Math.min(dragStart.y, dragCurrent.y);
    const width = Math.abs(dragStart.x - dragCurrent.x);
    const height = Math.abs(dragStart.y - dragCurrent.y);
    if (![left, top, width, height].every((n) => typeof n === 'number' && Number.isFinite(n))) {
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
      return;
    }
    const box = { left, top, width, height };

    if (!dragMovedRef.current) {
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
      return;
    }

    const nextSel: Record<number, boolean> = { ...selected };
    for (const { f, idx } of currentPageFields) {
      const r = toScreenRect(f);
      if (!r) continue;
      if (rectIntersects(box, r)) nextSel[idx] = true;
    }
    setSelected(nextSel);

    setIsDragging(false);
    setDragStart(null);
    setDragCurrent(null);
  };

  const renameGroup = (fromRaw: string, toRaw: string) => {
    const from = String(fromRaw || '').replace(/\s+/g, ' ').trim();
    const to = String(toRaw || '').replace(/\s+/g, ' ').trim().slice(0, 120);
    if (!from || !to || from === to) return;

    const next = fields.map((f) => {
      const g = String(f?.groupName || '').replace(/\s+/g, ' ').trim();
      if (g !== from) return f;
      return { ...f, groupName: to };
    });
    updateFields(next);

    if (existingGroupSelected === from) setExistingGroupSelected(to);
    if (renameFrom === from) setRenameFrom(to);
    setRenameTo('');
  };

  const clearGroupFromSelection = () => {
    const next = [...fields];
    next.forEach((f, idx) => {
      if (!selected[idx]) return;
      const { groupName: _, ...rest } = f || {};
      next[idx] = { ...rest, groupName: undefined };
    });

    updateFields(next);
  };

  const autoGroup = () => {
    const counts = new Map<string, number>();
    const candidates: Array<string | null> = fields.map((f) => inferGroupCandidate(String(f?.displayLabel || '')));

    for (const c of candidates) {
      if (!c) continue;
      counts.set(c, (counts.get(c) || 0) + 1);
    }

    const eligible = new Set(Array.from(counts.entries()).filter(([, n]) => n >= 3).map(([k]) => k));

    const next = fields.map((f, idx) => {
      const c = candidates[idx];
      const groupName = c && eligible.has(c) ? c : undefined;
      return { ...f, groupName };
    });

    updateFields(next);
  };

  if (!fields.length) {
    return (
      <div className="step-form">
        <div className="step-header">
          <h3 className="step-form-title">Preview & Grouping</h3>
          <p className="step-form-subtitle">Extract fields first, then return here to group them.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Preview & Grouping</h3>
        <p className="step-form-subtitle">Preview the PDF and organise fields into groups for faster filling. ({BUILD_TAG})</p>
      </div>

      <div className="step-form-content">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-[#E6E6E4] bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E6E6E4]">
              <div className="flex items-center gap-2">
                <div className="text-sm font-semibold text-[#181818]">PDF Preview</div>
                <div className="rounded-md border border-[#E6E6E4] bg-[#FAFAF9] px-2 py-0.5 text-[10px] font-semibold text-[#686E6C]">
                  {BUILD_TAG}
                </div>
              </div>
              {previewUrl ? (
                <a href={previewUrl} target="_blank" rel="noreferrer" className="btn btn-ghost">
                  Open
                </a>
              ) : null}
            </div>
            <div className="h-[560px] bg-[#FAFAF9] overflow-auto">
              {previewUrl ? (
                <div className="h-full w-full flex flex-col">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-[#E6E6E4] bg-white">
                    <div className="text-xs text-[#686E6C]">Interactive preview (drag to select fields).</div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={currentPageIndex <= 0}
                        onClick={() => setCurrentPageIndex((p) => Math.max(0, p - 1))}
                      >
                        Prev
                      </button>
                      <div className="text-xs text-[#686E6C]">
                        Page {currentPageIndex + 1}{numPages ? ` / ${numPages}` : ''}
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={numPages ? currentPageIndex >= numPages - 1 : false}
                        onClick={() => setCurrentPageIndex((p) => (numPages ? Math.min(numPages - 1, p + 1) : p + 1))}
                      >
                        Next
                      </button>
                    </div>
                  </div>

                  <div ref={pageContainerRef} className="w-full flex-1 overflow-auto p-3">
                    <div className="mb-2 rounded-md border border-[#E6E6E4] bg-white px-2 py-1 text-[11px] font-medium text-[#181818]">
                      Interactive canvas preview active: {BUILD_TAG}
                    </div>
                    <div className="mb-2 text-[11px] text-[#686E6C]">
                      dims:{' '}
                      {pageDims ? `${Math.round(pageDims.width)}x${Math.round(pageDims.height)} @ (${Math.round(pageDims.xMin)},${Math.round(pageDims.yMin)})` : '—'}
                      {' | '}overlay:{' '}
                      {overlaySize ? `${Math.round(overlaySize.width)}x${Math.round(overlaySize.height)}` : '—'}
                      {' | '}coords:{' '}
                      {fields.filter((f) => [f?.x, f?.y, f?.width, f?.height].every((n) => typeof n === 'number' && Number.isFinite(n))).length}/{fields.length}
                    </div>
                    {pdfError ? (
                      <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                        Failed to load PDF: {pdfError}
                      </div>
                    ) : null}

                    <div className="relative mx-auto" style={{ width: pageWidth || '100%', height: renderHeight || undefined }}>
                      <Document
                        file={previewUrl}
                        onLoadError={(e: any) => setPdfError(String(e?.message || e))}
                        loading={<div className="text-xs text-[#686E6C]">Loading PDF…</div>}
                        onLoadSuccess={(doc) => {
                          setPdfError('');
                          const n = Number((doc as any)?.numPages);
                          setNumPages(Number.isFinite(n) ? n : 0);
                        }}
                      >
                        <Page
                          pageNumber={currentPageIndex + 1}
                          width={effectivePageWidth}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          onLoadSuccess={(page: any) => {
                            try {
                              const view: any[] | null = Array.isArray(page?.view) ? page.view : null;
                              const x0 = view ? Number(view[0]) : NaN;
                              const y0 = view ? Number(view[1]) : NaN;
                              const x1 = view ? Number(view[2]) : NaN;
                              const y1 = view ? Number(view[3]) : NaN;
                              const vw = Number.isFinite(x0) && Number.isFinite(x1) ? x1 - x0 : NaN;
                              const vh = Number.isFinite(y0) && Number.isFinite(y1) ? y1 - y0 : NaN;
                              if (Number.isFinite(vw) && Number.isFinite(vh) && vw > 0 && vh > 0) {
                                setPageDims({ xMin: x0, yMin: y0, width: vw, height: vh });
                                return;
                              }

                              const meta = templatePages?.[currentPageIndex];
                              const mw = Number(meta?.width);
                              const mh = Number(meta?.height);
                              if (Number.isFinite(mw) && Number.isFinite(mh) && mw > 0 && mh > 0) {
                                const xMin = Number(meta?.xMin);
                                const yMin = Number(meta?.yMin);
                                setPageDims({
                                  xMin: Number.isFinite(xMin) ? xMin : 0,
                                  yMin: Number.isFinite(yMin) ? yMin : 0,
                                  width: mw,
                                  height: mh,
                                });
                                return;
                              }

                              const vp = typeof page?.getViewport === 'function' ? page.getViewport({ scale: 1 }) : null;
                              const w = Number(vp?.width);
                              const h = Number(vp?.height);
                              if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
                                setPageDims({ xMin: 0, yMin: 0, width: w, height: h });
                              }
                            } catch {
                              // ignore
                            }
                          }}
                        />
                      </Document>

                      <div
                        ref={overlayRef}
                        className="absolute inset-0 z-50"
                        onPointerDownCapture={handlePointerDown}
                        onPointerMoveCapture={handlePointerMove}
                        onPointerUpCapture={handlePointerUp}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onMouseDown={handlePointerDown}
                        onMouseMove={handlePointerMove}
                        onMouseUp={handlePointerUp}
                        onPointerLeave={() => handlePointerUp()}
                        onPointerCancel={() => handlePointerUp()}
                        onContextMenu={(e) => e.preventDefault()}
                        style={{
                          touchAction: 'none',
                          cursor: isDragging ? 'crosshair' : 'default',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          pointerEvents: 'auto',
                        }}
                      >
                        {currentPageFields.map(({ f, idx }) => {
                          const r = toScreenRect(f);
                          if (!r) return null;
                          const isSel = Boolean(selected[idx]);
                          const g = String(f?.groupName || '').trim();
                          const border = isSel ? '2px solid #F97316' : g ? '1px solid #10B981' : '1px solid rgba(17, 24, 39, 0.25)';
                          const bg = isSel ? 'rgba(249, 115, 22, 0.12)' : g ? 'rgba(16, 185, 129, 0.08)' : 'rgba(17, 24, 39, 0.04)';
                          return (
                            <button
                              key={String(f.pdfFieldName || idx)}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (dragMovedRef.current) return;
                                setSelected((prev) => ({ ...prev, [idx]: !prev[idx] }));
                              }}
                              title={`${String(f.displayLabel || '')}${g ? ` (${g})` : ''}`}
                              style={{
                                position: 'absolute',
                                left: r.left,
                                top: r.top,
                                width: r.width,
                                height: r.height,
                                border,
                                background: bg,
                                borderRadius: 4,
                                padding: 0,
                                cursor: 'pointer',
                              }}
                            />
                          );
                        })}

                        {isDragging && dragStart && dragCurrent ? (
                          <div
                            style={{
                              position: 'absolute',
                              left: Math.min(dragStart.x, dragCurrent.x),
                              top: Math.min(dragStart.y, dragCurrent.y),
                              width: Math.abs(dragStart.x - dragCurrent.x),
                              height: Math.abs(dragStart.y - dragCurrent.y),
                              border: '1px dashed #F97316',
                              background: 'rgba(249, 115, 22, 0.08)',
                              borderRadius: 4,
                            }}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-sm text-[#686E6C]">No preview available.</div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-[#E6E6E4] bg-white">
            <div className="px-4 py-3 border-b border-[#E6E6E4]">
              <div className="text-sm font-semibold text-[#181818]">Grouping</div>
              <div className="mt-1 text-xs text-[#686E6C]">
                Each field can belong to exactly one group. Grouping is optional.
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="btn btn-secondary" onClick={autoGroup}>
                  Auto-group
                </button>

                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => toggleAll(selectionCount !== fields.length)}
                >
                  {selectionCount === fields.length ? 'Clear selection' : 'Select all'}
                </button>

                <div className="ml-auto text-xs text-[#686E6C]">
                  Selected: {selectionCount}/{fields.length}
                </div>
              </div>

              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                {existingGroups.length ? (
                  <select
                    className="form-input"
                    value={existingGroupSelected}
                    onChange={(e) => setExistingGroupSelected(e.target.value)}
                  >
                    {existingGroups.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                ) : null}

                <input
                  className="form-input"
                  placeholder="Group name (e.g. Appliance 1)"
                  value={groupNameInput}
                  onChange={(e) => setGroupNameInput(e.target.value)}
                />

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={selectionCount === 0 || (!String(groupNameInput || '').trim() && !existingGroupSelected)}
                    onClick={() => applyGroupToSelection(String(groupNameInput || '').trim() ? groupNameInput : existingGroupSelected)}
                  >
                    Assign
                  </button>

                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={selectionCount === 0}
                    onClick={clearGroupFromSelection}
                  >
                    Clear
                  </button>
                </div>
              </div>

              {groupSummary.length ? (
                <div className="rounded-lg border border-[#E6E6E4] bg-[#FAFAF9] p-3">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9CA3AF]">
                    Current groups
                  </div>
                  <div className="mt-2 space-y-1">
                    {groupSummary.map((g) => (
                      <div key={g.name} className="flex items-center justify-between text-xs text-[#383838]">
                        <div className="truncate">{g.name}</div>
                        <div className="text-[#686E6C]">{g.count}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {existingGroups.length ? (
                <div className="rounded-lg border border-[#E6E6E4] bg-white p-3">
                  <div className="text-xs font-semibold text-[#181818]">Rename group</div>
                  <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                    <select className="form-input" value={renameFrom} onChange={(e) => setRenameFrom(e.target.value)}>
                      {existingGroups.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                    <input
                      className="form-input"
                      placeholder="New name"
                      value={renameTo}
                      onChange={(e) => setRenameTo(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={!String(renameFrom || '').trim() || !String(renameTo || '').trim()}
                      onClick={() => renameGroup(renameFrom, renameTo)}
                    >
                      Rename
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="max-h-[340px] overflow-auto rounded-lg border border-[#E6E6E4]">
                <table className="min-w-full text-left text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-[#E6E6E4]">
                      <th className="px-3 py-2 w-10">
                        <input
                          type="checkbox"
                          checked={selectionCount === fields.length && fields.length > 0}
                          onChange={(e) => toggleAll(e.target.checked)}
                        />
                      </th>
                      <th className="px-3 py-2">Label</th>
                      <th className="px-3 py-2">Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f, idx) => (
                      <tr key={f.pdfFieldName || idx} className="border-b border-[#E6E6E4]">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={Boolean(selected[idx])}
                            onChange={(e) => setSelected((prev) => ({ ...prev, [idx]: e.target.checked }))}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-[#181818] truncate max-w-[260px]">
                            {String(f.displayLabel || 'Untitled')}
                          </div>
                          <div className="text-[#686E6C] truncate max-w-[260px]">{String(f.pdfFieldName || '')}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-[#383838] truncate max-w-[220px]">{String(f.groupName || '—')}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
